import { Button, HTMLTable, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Banner } from "components/common/Banner";
import { NiraDialog } from "components/common/NiraDialog";
import { useRegionConfig } from "in_queue/contexts/ClusterContext";
import { uniq } from "lodash";
import { ReceivedReduction } from "../ScenarioDialog/sensitivitiesCsv";
import { BulkScenarioDraft, bulkScenarioNotices } from "./bulkScenarioDrafts";
import css from "./BulkScenarioWarningsDialog.module.scss";

const WARNINGS_EYEBROW = "Upload warnings";
const PROJECT_ID_HEADER = "Project ID";
const BLANK_PROJECT_ID = "(blank)";
const EMPTY_CELL = "-";

const projectIdText = (projectId: string): string =>
    projectId.trim() === "" ? BLANK_PROJECT_ID : projectId;

const cellText = (text: string | undefined): string =>
    text === undefined || text.trim() === "" ? EMPTY_CELL : text;

const ReceivedReductionsTable: React.FC<{
    readonly projectIds: ReadonlyArray<string>;
    readonly receivedReductions: Readonly<Record<string, ReceivedReduction>>;
    readonly onlyExclusionSupported: boolean;
}> = ({ projectIds, receivedReductions, onlyExclusionSupported }) => {
    const { sizeLabel } = useRegionConfig();

    return (
        <HTMLTable
            compact
            striped
            className={css["BulkScenarioWarningsDialog-table"]}
        >
            <thead>
                <tr>
                    <th>{PROJECT_ID_HEADER}</th>
                    {!onlyExclusionSupported && (
                        <>
                            <th>{sizeLabel.energyLabel}</th>
                            <th>{sizeLabel.capacityLabel}</th>
                        </>
                    )}
                </tr>
            </thead>
            <tbody>
                {uniq(projectIds).map((projectId) => {
                    const received: ReceivedReduction | undefined =
                        receivedReductions[projectId];
                    return (
                        <tr key={projectId}>
                            <td>{projectIdText(projectId)}</td>
                            {!onlyExclusionSupported && (
                                <>
                                    <td>{cellText(received?.eris)}</td>
                                    <td>{cellText(received?.nris)}</td>
                                </>
                            )}
                        </tr>
                    );
                })}
            </tbody>
        </HTMLTable>
    );
};

export const BulkScenarioWarningsDialog: React.FC<{
    readonly draft: BulkScenarioDraft;
    readonly onlyExclusionSupported: boolean;
    readonly onClose: () => void;
}> = ({ draft, onlyExclusionSupported, onClose }) => {
    const warnings = bulkScenarioNotices(
        draft.uploadNotes,
        onlyExclusionSupported
    ).filter((notice) => notice.intent === Intent.WARNING);

    return (
        <NiraDialog
            isOpen
            onClose={onClose}
            eyebrow={WARNINGS_EYEBROW}
            title={draft.title}
            className={css["BulkScenarioWarningsDialog"]}
            bodyClassName={css["BulkScenarioWarningsDialog-body"]}
            gap="small"
            footerActions={
                <Button text="Close" onClick={onClose} size="large" />
            }
        >
            {warnings.map((notice) => (
                <div
                    key={notice.title}
                    className={css["BulkScenarioWarningsDialog-warning"]}
                >
                    <Banner
                        intent={Intent.WARNING}
                        icon={IconNames.WARNING_SIGN}
                        title={notice.title}
                        description={notice.description}
                    />
                    <ReceivedReductionsTable
                        projectIds={notice.projectIds}
                        receivedReductions={
                            draft.uploadNotes.receivedReductions
                        }
                        onlyExclusionSupported={onlyExclusionSupported}
                    />
                </div>
            ))}
        </NiraDialog>
    );
};
